/**
 * Typed client + TanStack Query hooks for the F2 video module.
 *
 * Endpoints (backend/app/video/router.py):
 *   GET    /levantamentos/{id}                       -> LevantamentoDetailOut
 *   GET    /levantamentos/{id}/scene                 -> SceneResponse
 *   GET    /levantamentos/{id}/scene/reference.jpg   (binary)
 *   GET    /levantamentos/{id}/scene/trajectories.geojson
 *   GET    /levantamentos/{id}/cut-lines             -> CutLineOut[]
 *   POST   /levantamentos/{id}/cut-lines             -> CutLineOut
 *   PATCH  /levantamentos/{id}/cut-lines/{cid}       -> CutLineOut
 *   DELETE /levantamentos/{id}/cut-lines/{cid}       -> 204
 *   POST   /levantamentos/{id}/count                 -> CountRunOut
 *   DELETE /levantamentos/{id}/count-runs/{run}      -> 204 No Content (soft-delete)
 *   GET    /levantamentos/{id}/count-runs/{run}/TMC.xlsx          (?original=true — #488)
 *   GET    /levantamentos/{id}/count-runs/{run}/VehicleList.xlsx  (?original=true — #488)
 *   GET    /levantamentos/{id}/count-runs/{run}/contagens.csv     (?original=true — #488)
 *   --- #299 corredor de entrada ---
 *   GET    /videos/inbox                             -> VideoInboxItem[]
 *   POST   /videos/inbox/scan                        -> InboxScanResult
 *   GET    /videos/config                            -> VideoInboxConfig (+ fila_max_concurrent, #426)
 *   POST   /levantamentos/{id}/videos                -> VideoOut[]
 *   POST   /levantamentos/{id}/process               -> ProcessResponse
 *   POST   /levantamentos/{id}/dispatch               -> LevantamentoOut (#423/#426 — placar/reprocessar)
 *   POST   /levantamentos/{id}/reprocessar             -> LevantamentoOut (#434 — clone com vínculo + vigência)
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { getNkToken } from "@/lib/auth-token";
import { hasFilaEmAndamento } from "@/lib/fila-helpers";
import type {
  AnnotationCorrectInput,
  AnnotationDiscardInput,
  AnnotationOut,
  AnnotationPriority,
  AnnotationQueue,
  AnnotationsListResponse,
  BulkAnnotationInput,
  BulkAnnotationResult,
  CarimboCountOut,
  CountRequest,
  UndoLastBatchResult,
  CountRunDetail,
  CountRunOut,
  CountRunSummary,
  CutLineCreate,
  CutLineOut,
  CutLineUpdate,
  InboxScanResult,
  JanelaAnalise,
  LevantamentoDetailOut,
  LevantamentoOut,
  LevantamentoStatus,
  ProcessResponse,
  SceneResponse,
  TriagemSummary,
  ValidateLevantamentoResponse,
  VideoAssociateInput,
  VideoInboxConfig,
  VideoInboxItem,
  VideoOut,
} from "@/types/video";

// ---------------------------------------------------------------------------
// Raw fetch funcs
// ---------------------------------------------------------------------------

export interface ListLevantamentosFilter {
  projeto_id?: string;
  status?: LevantamentoStatus;
}

// #237 — create input type (mirrors backend LevantamentoCreate schema)
export interface LevantamentoCreate {
  projeto_id?: string | null;
  posto_contagem: string;
  rodovia?: string | null;
  km?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  sentido: string;
  movimento?: string | null;
  data_hora_inicial: string; // ISO datetime string
  janelas_analise?: JanelaAnalise[] | null;
}

/** GET /levantamentos — top-level list. TD-VIDEO-RESCOPE Phase 1: retorna
 * tanto Levantamentos vinculados a projetos do user quanto órfãos
 * (projeto_id IS NULL, tenant-shared). Filter por projeto_id retorna só
 * vinculados àquele projeto (legacy filtered view). */
export const listLevantamentos = (filter: ListLevantamentosFilter = {}) => {
  const qs = new URLSearchParams();
  if (filter.projeto_id) qs.set("projeto_id", filter.projeto_id);
  if (filter.status) qs.set("status", filter.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api.get<LevantamentoOut[]>(`/levantamentos${suffix}`);
};

/** POST /levantamentos — criação manual de levantamento (#237). */
export const createLevantamento = (data: LevantamentoCreate) =>
  api.post<LevantamentoOut>("/levantamentos", data);

// #300 Part 2 — update input type (mirrors backend LevantamentoUpdate schema).
// All fields optional: PATCH accepts any subset.
// #331 — expanded with all editable metadata fields.
export interface LevantamentoUpdate {
  posto_contagem?: string;
  rodovia?: string | null;
  km?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  sentido?: string;
  movimento?: string | null;
  janelas_analise?: JanelaAnalise[] | null;
}

/** PATCH /levantamentos/{id} — partial update.
 * Used by the janela editor on the conference screen to set/clear windows. */
export const patchLevantamento = (levId: string, data: LevantamentoUpdate) =>
  api.patch<LevantamentoOut>(`/levantamentos/${levId}`, data);

export const getLevantamento = (levId: string) =>
  api.get<LevantamentoDetailOut>(`/levantamentos/${levId}`);

export const getScene = (levId: string) =>
  api.get<SceneResponse>(`/levantamentos/${levId}/scene`);

export const listCutLines = (levId: string) =>
  api.get<CutLineOut[]>(`/levantamentos/${levId}/cut-lines`);

export const createCutLine = (levId: string, data: CutLineCreate) =>
  api.post<CutLineOut>(`/levantamentos/${levId}/cut-lines`, data);

export const patchCutLine = (
  levId: string,
  cutId: string,
  data: CutLineUpdate,
) =>
  api.patch<CutLineOut>(`/levantamentos/${levId}/cut-lines/${cutId}`, data);

export const deleteCutLine = (levId: string, cutId: string) =>
  api.delete<void>(`/levantamentos/${levId}/cut-lines/${cutId}`);

export const runCount = (levId: string, data: CountRequest) =>
  api.post<CountRunOut>(`/levantamentos/${levId}/count`, data);

export const listCountRuns = (levId: string) =>
  api.get<CountRunSummary[]>(`/levantamentos/${levId}/count-runs`);

export const getCountRunDetail = (levId: string, countRunId: string) =>
  api.get<CountRunDetail>(`/levantamentos/${levId}/count-runs/${countRunId}`);

export const deleteCountRun = (levId: string, countRunId: string) =>
  api.delete<void>(`/levantamentos/${levId}/count-runs/${countRunId}`);

export type CountArtifact = "TMC" | "VehicleList";

// #488 — `original` adiciona `?original=true`: serve o snapshot pré-revisão
// (TMC/VehicleList gravados em disco no /count; CSV ignorando corrected_class/
// is_discarded). Default (false/omitido) preserva o comportamento "revisado"
// (regenera em memória refletindo as correções do operador).
export const countArtifactPath = (
  levId: string,
  countRunId: string,
  kind: CountArtifact,
  original = false,
) =>
  `/levantamentos/${levId}/count-runs/${countRunId}/${kind}.xlsx${
    original ? "?original=true" : ""
  }`;

// #301 — CSV bridge: contagens.csv download path
export const contagensCsvPath = (
  levId: string,
  countRunId: string,
  original = false,
) =>
  `/levantamentos/${levId}/count-runs/${countRunId}/contagens.csv${
    original ? "?original=true" : ""
  }`;

// ---------------------------------------------------------------------------
// Asset URL resolver
// ---------------------------------------------------------------------------

/**
 * Backend returns scene asset URLs as absolute paths starting with `/api/v1/...`.
 * NEXT_PUBLIC_API_URL is `http(s)://host/api/v1`. Strip the suffix from the base
 * before concatenating to avoid `/api/v1/api/v1/...`.
 */
export function sceneAssetUrl(relativeUrl: string): string {
  const base =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";
  if (relativeUrl.startsWith("http://") || relativeUrl.startsWith("https://")) {
    return relativeUrl;
  }
  if (base.endsWith("/api/v1") && relativeUrl.startsWith("/api/v1")) {
    return base.replace(/\/api\/v1$/, "") + relativeUrl;
  }
  return `${base}${relativeUrl.startsWith("/") ? "" : "/"}${relativeUrl}`;
}

/**
 * URL da página de upload retomável servida na RAIZ do host da API (fora do
 * prefixo /api/v1) — ver `backend/app/video_main.py` `GET "/"`, que retorna
 * `video/static/upload.html`. #391 Parte A.3: link exposto na lista de
 * levantamentos pra o operador subir vídeo direto, sem depender do worker de
 * inbox.
 */
export function videoUploadPageUrl(): string {
  const base =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";
  return base.replace(/\/api\/v1\/?$/, "") || base;
}

// ---------------------------------------------------------------------------
// Authed binary download (xlsx, geojson, jpg) — fetch with Bearer
// ---------------------------------------------------------------------------

async function authedFetch(url: string): Promise<Response> {
  const token = getNkToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res;
}

export async function downloadCountArtifact(
  levId: string,
  countRunId: string,
  kind: CountArtifact,
  original = false,
): Promise<Blob> {
  const base =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";
  const res = await authedFetch(
    `${base}${countArtifactPath(levId, countRunId, kind, original)}`,
  );
  return res.blob();
}

// #301 — download contagens.csv for a given count_run (blob → browser save)
export async function downloadContagensCsv(
  levId: string,
  countRunId: string,
  original = false,
): Promise<Blob> {
  const base =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";
  const res = await authedFetch(
    `${base}${contagensCsvPath(levId, countRunId, original)}`,
  );
  return res.blob();
}

export async function fetchTrajectoriesGeoJson<T>(absoluteUrl: string): Promise<T> {
  const res = await authedFetch(absoluteUrl);
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const videoQueryKeys = {
  levantamentos: (filter?: ListLevantamentosFilter) =>
    ["levantamentos", "list", filter ?? null] as const,
  levantamento: (id: string) => ["levantamento", id] as const,
  scene: (id: string) => ["levantamento", id, "scene"] as const,
  cutLines: (id: string) => ["levantamento", id, "cut-lines"] as const,
  countRuns: (id: string) => ["levantamento", id, "count-runs"] as const,
  countRunDetail: (levId: string, runId: string) =>
    ["levantamento", levId, "count-runs", runId] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useLevantamentos(filter: ListLevantamentosFilter = {}) {
  return useQuery({
    queryKey: videoQueryKeys.levantamentos(filter),
    queryFn: () => listLevantamentos(filter),
    // #426 review — o placar da fila (posição FIFO/badge) deriva desta
    // lista; sem isso ela só reflete o avanço do dispatcher em background
    // no próximo remount/invalidation. Poll só enquanto ALGUM levantamento
    // visível está em_andamento (na_fila/processando/pos_processamento) —
    // não pollar à toa com a fila vazia/parada.
    refetchInterval: (query) =>
      hasFilaEmAndamento(query.state.data) ? 15_000 : false,
  });
}

export function useCreateLevantamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LevantamentoCreate) => createLevantamento(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamentos() });
    },
  });
}

/** usePatchLevantamento — PATCH /levantamentos/{id}.
 * Invalida o detalhe do levantamento e a lista geral após PATCH bem-sucedido. */
export function usePatchLevantamento(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LevantamentoUpdate) => patchLevantamento(levId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamento(levId) });
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamentos() });
    },
  });
}

export function useLevantamento(levId: string) {
  return useQuery({
    queryKey: videoQueryKeys.levantamento(levId),
    queryFn: () => getLevantamento(levId),
    enabled: !!levId,
    // #426 review — mesmo gate de useLevantamentos: poll só enquanto ESTE
    // levantamento está em_andamento; parado/erro/pronto não pollam à toa.
    refetchInterval: (query) =>
      hasFilaEmAndamento(query.state.data) ? 15_000 : false,
  });
}

export function useScene(levId: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: videoQueryKeys.scene(levId),
    queryFn: () => getScene(levId),
    enabled: !!levId && opts.enabled !== false,
  });
}

export function useCutLines(levId: string) {
  return useQuery({
    queryKey: videoQueryKeys.cutLines(levId),
    queryFn: () => listCutLines(levId),
    enabled: !!levId,
  });
}

export function useCreateCutLine(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CutLineCreate) => createCutLine(levId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.cutLines(levId) });
    },
  });
}

export function usePatchCutLine(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cutId, data }: { cutId: string; data: CutLineUpdate }) =>
      patchCutLine(levId, cutId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.cutLines(levId) });
    },
  });
}

export function useDeleteCutLine(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cutId: string) => deleteCutLine(levId, cutId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.cutLines(levId) });
    },
  });
}

export function useRunCount(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CountRequest) => runCount(levId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.countRuns(levId) });
    },
  });
}

export function useCountRuns(levId: string) {
  return useQuery({
    queryKey: videoQueryKeys.countRuns(levId),
    queryFn: () => listCountRuns(levId),
    enabled: !!levId,
  });
}

export function useCountRunDetail(levId: string, countRunId: string) {
  return useQuery({
    queryKey: videoQueryKeys.countRunDetail(levId, countRunId),
    queryFn: () => getCountRunDetail(levId, countRunId),
    enabled: !!levId && !!countRunId,
  });
}

export function useDeleteCountRun(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (countRunId: string) => deleteCountRun(levId, countRunId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.countRuns(levId) });
    },
  });
}

// ---------------------------------------------------------------------------
// F2 Phase 1.5 — annotations + tenant config (TD-VIDEO-PHASE15-1)
// ---------------------------------------------------------------------------

export interface ListAnnotationsParams {
  priority?: AnnotationPriority;
  /** #242 — Fila de revisão. Toma precedência sobre priority quando fornecido.
   * 'revisar' = bucket high (pesados + baixa-confiança, pendentes).
   * 'caminhoes' = todo predicted_class='truck' (qualquer confiança, pendente). */
  queue?: AnnotationQueue;
  page?: number;
  page_size?: number;
  /** Filtra annotations apenas do count_run específico — sem ele o backend
   * retorna a união de todos os count_runs do levantamento (múltiplas
   * execuções de /count empilham crossings independentes). */
  count_run_id?: string;
  /** #242 — Filtra a fila a uma cut_line de cada vez (UUID). */
  cut_line_id?: string;
  /** #302 — Lente de proveniência (origem da classe), ortogonal às filas
   * (ADR-VID-003). SOBREPÕE queue/priority no backend: lista por origem em todo
   * o levantamento (escopo run + cut_line preservado). 'corrected' devolve os
   * resolvidos — invisíveis nas filas de trabalho — pra auditoria. */
  provenance?: "bruto" | "refined" | "corrected";
}

export const listAnnotations = (
  levId: string,
  params: ListAnnotationsParams = {},
) => {
  const qs = new URLSearchParams();
  if (params.priority) qs.set("priority", params.priority);
  if (params.queue) qs.set("queue", params.queue);
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));
  if (params.count_run_id) qs.set("count_run_id", params.count_run_id);
  if (params.cut_line_id) qs.set("cut_line_id", params.cut_line_id);
  if (params.provenance) qs.set("provenance", params.provenance);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api.get<AnnotationsListResponse>(
    `/levantamentos/${levId}/annotations${suffix}`,
  );
};

export interface TriagemSummaryParams {
  /** Filtra ao count_run específico; sem ele runs soft-deletados são excluídos (#216). */
  count_run_id?: string;
  /** #242 — restringe a triagem a uma cut_line de cada vez. */
  cut_line_id?: string;
}

/** GET /levantamentos/{lev_id}/annotations/triagem-summary — contadores de triagem
 * (obrigatória/opcional) + disclosure anti-fabricação (#243). Read-only. */
export const getTriagemSummary = (
  levId: string,
  params: TriagemSummaryParams = {},
): Promise<TriagemSummary> => {
  const qs = new URLSearchParams();
  if (params.count_run_id) qs.set("count_run_id", params.count_run_id);
  if (params.cut_line_id) qs.set("cut_line_id", params.cut_line_id);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api.get<TriagemSummary>(
    `/levantamentos/${levId}/annotations/triagem-summary${suffix}`,
  );
};

export const correctAnnotation = (
  annotationId: string,
  data: AnnotationCorrectInput,
) => api.patch<AnnotationOut>(`/annotations/${annotationId}`, data);

/** #204 — PATCH is_discarded (ortogonal a corrected_class). */
export const discardAnnotation = (
  annotationId: string,
  data: AnnotationDiscardInput,
) => api.patch<AnnotationOut>(`/annotations/${annotationId}`, data);

export const validateLevantamento = (levId: string) =>
  api.post<ValidateLevantamentoResponse>(
    `/levantamentos/${levId}/validate`,
    {},
  );

/**
 * URLs absolute serving thumbnails / clips. Auth via Bearer token —
 * `<img>` / `<video>` tags don't carry headers, so caller usa fetch+blob+ObjectURL
 * pattern OU embute token via query param custom (não fazemos isso, é ruim).
 * Componente Image (Wave 5) usa authedFetch + URL.createObjectURL.
 */
export function annotationThumbnailUrl(annotationId: string): string {
  const base =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";
  return `${base}/annotations/${annotationId}/thumbnail`;
}

export function crossingClipUrl(
  levId: string,
  crossingId: string,
  duration: number = 2,
): string {
  const base =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";
  return `${base}/levantamentos/${levId}/clips/${crossingId}?duration=${duration}`;
}

export async function fetchAuthenticatedBlob(
  absoluteUrl: string,
): Promise<Blob> {
  const res = await authedFetch(absoluteUrl);
  return res.blob();
}

// ---------------------------------------------------------------------------
// Phase 1.5 query keys + hooks
// ---------------------------------------------------------------------------

export const phase15QueryKeys = {
  annotations: (levId: string, params: ListAnnotationsParams = {}) =>
    ["levantamento", levId, "annotations", params] as const,
  /** #243 — sob o prefixo ["levantamento", levId, "annotations", ...] de propósito:
   * as mutations (correct/discard/bulk/validate) já invalidam esse prefixo, então
   * a triagem refaz fetch ao vivo após cada PATCH sem fiação extra. */
  triagemSummary: (levId: string, params: TriagemSummaryParams = {}) =>
    ["levantamento", levId, "annotations", "triagem-summary", params] as const,
};

export function useAnnotations(
  levId: string,
  params: ListAnnotationsParams = {},
) {
  return useQuery({
    queryKey: phase15QueryKeys.annotations(levId, params),
    queryFn: () => listAnnotations(levId, params),
    enabled: !!levId,
  });
}

/** #243 — Contadores de triagem (obrigatória/opcional) + disclosure.
 * Keyed sob ["levantamento", levId, "annotations", "triagem-summary"], então
 * as invalidações de PATCH existentes o atualizam ao vivo. */
export function useTriagemSummary(
  levId: string,
  params: TriagemSummaryParams = {},
) {
  return useQuery({
    queryKey: phase15QueryKeys.triagemSummary(levId, params),
    queryFn: () => getTriagemSummary(levId, params),
    enabled: !!levId,
  });
}

export function useCorrectAnnotation(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      annotationId,
      data,
    }: {
      annotationId: string;
      data: AnnotationCorrectInput;
    }) => correctAnnotation(annotationId, data),
    onSuccess: () => {
      // Invalidate todas as variants paginadas + filtradas dessa lev.
      qc.invalidateQueries({
        queryKey: ["levantamento", levId, "annotations"],
      });
    },
  });
}

/** #204 — toggle is_discarded. Invalida as mesmas queries que useCorrectAnnotation. */
export function useDiscardAnnotation(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      annotationId,
      is_discarded,
    }: {
      annotationId: string;
      is_discarded: boolean;
    }) => discardAnnotation(annotationId, { is_discarded }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["levantamento", levId, "annotations"],
      });
    },
  });
}

export function useValidateLevantamento(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => validateLevantamento(levId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamento(levId) });
      qc.invalidateQueries({
        queryKey: ["levantamento", levId, "annotations"],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// #241 — bulk-update + carimbo-count (D6)
// ---------------------------------------------------------------------------

/** PATCH /annotations/bulk — aplica corrected_class e/ou is_discarded a um
 * conjunto explícito de annotation_ids passados pelo caller. O backend aplica
 * apenas aos ids listados (sem propagação automática). */
export const bulkUpdateAnnotations = (data: BulkAnnotationInput) =>
  api.patch<BulkAnnotationResult>("/annotations/bulk", data);

export interface CarimboCountParams {
  cut_line_id: string;
  predicted_class: string;
  conf_min: string;
  conf_max: string;
  count_run_id?: string;
}

/** GET /levantamentos/{lev_id}/annotations/carimbo-count — read-only preview
 * de quantas annotations pending batem o carimbo antes de propagar. */
export const getCarimboCount = (
  levId: string,
  params: CarimboCountParams,
): Promise<CarimboCountOut> => {
  const qs = new URLSearchParams();
  qs.set("cut_line_id", params.cut_line_id);
  qs.set("predicted_class", params.predicted_class);
  qs.set("conf_min", params.conf_min);
  qs.set("conf_max", params.conf_max);
  if (params.count_run_id) qs.set("count_run_id", params.count_run_id);
  return api.get<CarimboCountOut>(
    `/levantamentos/${levId}/annotations/carimbo-count?${qs.toString()}`,
  );
};

/** useBulkUpdateAnnotations — invalida todas as annotation queries do levantamento
 * após PATCH /annotations/bulk bem-sucedido (totais + listas refletem ao vivo). */
export function useBulkUpdateAnnotations(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkAnnotationInput) => bulkUpdateAnnotations(data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["levantamento", levId, "annotations"],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// #244 — desfazer último lote (reversibilidade de lote, D8 nível 2)
// ---------------------------------------------------------------------------

/** POST /levantamentos/{id}/annotations/undo-last-batch — desfaz o ÚLTIMO
 * carimbo/lote ativo como bloco, restaurando cada annotation ao estado prévio.
 * Sem body. Idempotente: sem lote ativo → reverted=false (200, no-op). */
export const undoLastBatch = (levId: string): Promise<UndoLastBatchResult> =>
  api.post<UndoLastBatchResult>(
    `/levantamentos/${levId}/annotations/undo-last-batch`,
    {},
  );

/** useUndoLastBatch — invalida o prefixo ["levantamento", levId, "annotations"]
 * após o undo, então lista + counts + triagem-summary re-derivam o número ao
 * vivo (mesmo padrão de useBulkUpdateAnnotations / useCorrectAnnotation). */
export function useUndoLastBatch(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => undoLastBatch(levId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["levantamento", levId, "annotations"],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// #299 — corredor de entrada: inbox, config, associate, process
// ---------------------------------------------------------------------------

/** GET /videos/inbox — lista vídeos detectados aguardando atribuição. */
export const listInboxVideos = (): Promise<VideoInboxItem[]> =>
  api.get<VideoInboxItem[]>("/videos/inbox");

/** POST /videos/inbox/scan — scan manual da pasta inbox (fecha G4: operador não
 * depende do loop de 10s do worker). */
export const scanInbox = (): Promise<InboxScanResult> =>
  api.post<InboxScanResult>("/videos/inbox/scan", {});

/** GET /videos/config — expõe o inbox_path configurado no servidor (fecha G6:
 * o path não fica chumbado no componente front). */
export const getVideoConfig = (): Promise<VideoInboxConfig> =>
  api.get<VideoInboxConfig>("/videos/config");

/** POST /levantamentos/{id}/videos — associa N vídeos da inbox ao levantamento.
 * Atômico: move inbox/→videos/{video_id}.mp4, persiste levantamento_id.
 * Retorna a lista VideoOut dos vídeos associados. */
export const associateVideos = (
  levId: string,
  data: VideoAssociateInput,
): Promise<VideoOut[]> =>
  api.post<VideoOut[]>(`/levantamentos/${levId}/videos`, data);

/** POST /levantamentos/{id}/process — marca vídeos como queued, lev→em_processamento.
 * BOTÃO EXPLÍCITO — NUNCA automático ao associar (D2 SPEC: processar é 4-6h de GPU;
 * operador anexa, confere ordem, ENTÃO processa). */
export const processLevantamento = (levId: string): Promise<ProcessResponse> =>
  api.post<ProcessResponse>(`/levantamentos/${levId}/process`, {});

/** POST /levantamentos/{id}/dispatch — #423/#426: flipa fila_status
 * aberto|erro → na_fila (mesmo endpoint do botão "Processar levantamento" e
 * do botão "Reprocessar" de um levantamento em erro — sem caminho paralelo,
 * ADR-VID-005 Q5). O enfileiramento por-vídeo real só acontece quando o
 * dispatcher CLAIMA o levantamento (respeitando teto/FIFO). */
export const dispatchLevantamento = (levId: string): Promise<LevantamentoOut> =>
  api.post<LevantamentoOut>(`/levantamentos/${levId}/dispatch`, {});

/** Body de POST /levantamentos/{id}/reprocessar (follow-up #434, mesmo
 * branch/PR). `clonar_linhas` default true no backend -- espelhado aqui
 * pro caller que não passar nada continuar recebendo o mesmo default. */
export interface ReprocessarLevantamentoInput {
  clonar_linhas?: boolean;
}

/** POST /levantamentos/{id}/reprocessar — #434: botão "Reprocessar" de um
 * levantamento JÁ VALIDADO. Cria um levantamento NOVO clonado (mesmos dados
 * de posto/rodovia/sentido/data + `origem_levantamento_id` -> o validado),
 * reatribui os mesmos vídeos ao clone (resetados, prontos pra reprocessar) e
 * devolve o clone -- o operador segue o fluxo normal a partir dele (anexar
 * cut-lines, "Processar levantamento"). O validado original permanece
 * intocado; 409 se o levantamento ainda não estiver 'validated'.
 *
 * `clonar_linhas` (default true, follow-up #434): copia as cut-lines ativas
 * do original em rows NOVAS pro clone -- caso típico é mesma câmera/
 * enquadramento, então clonar poupa redesenhar do zero. false = clone nasce
 * sem cut-lines (comportamento original da fatia). */
export const reprocessarLevantamento = (
  levId: string,
  data: ReprocessarLevantamentoInput = {},
): Promise<LevantamentoOut> =>
  api.post<LevantamentoOut>(`/levantamentos/${levId}/reprocessar`, data);

// Query keys
export const inboxQueryKeys = {
  inbox: () => ["videos", "inbox"] as const,
  config: () => ["videos", "config"] as const,
};

/** useInboxVideos — lista vídeos detectados aguardando atribuição (inbox). */
export function useInboxVideos() {
  return useQuery({
    queryKey: inboxQueryKeys.inbox(),
    queryFn: listInboxVideos,
  });
}

/** useVideoConfig — expõe inbox_path + fila_max_concurrent do servidor
 * para componentes front (G6 / #426). Sem refetchInterval de propósito
 * (#426 review): ao contrário de useLevantamento(s), o valor aqui é
 * config estática de deploy (env var), não estado de fila que avança
 * sozinho no backend — não há "algo em andamento" pra observar, então
 * pollar não traria dado mais fresco, só custo. staleTime cobre a
 * cadência de refresh que faz sentido pra este dado. */
export function useVideoConfig() {
  return useQuery({
    queryKey: inboxQueryKeys.config(),
    queryFn: getVideoConfig,
    // Config muda raramente: stale após 5min, fresh após 1min
    staleTime: 60_000,
  });
}

/** useScanInbox — dispara scan manual da inbox (fecha G4). Invalida lista inbox. */
export function useScanInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: scanInbox,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inboxQueryKeys.inbox() });
    },
  });
}

/** useAssociateVideos — associa vídeos da inbox ao levantamento.
 * Invalida detalhe do levantamento (vídeos aparecem na lista) + inbox (remove associados). */
export function useAssociateVideos(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: VideoAssociateInput) => associateVideos(levId, data),
    onSuccess: () => {
      // Levantamento detail atualiza com videos associados
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamento(levId) });
      // Inbox remove os vídeos agora associados
      qc.invalidateQueries({ queryKey: inboxQueryKeys.inbox() });
    },
  });
}

/** useProcessLevantamento — submete levantamento para processamento (botão explícito).
 * Invalida detalhe do levantamento (status muda para em_processamento) + lista geral. */
export function useProcessLevantamento(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => processLevantamento(levId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamento(levId) });
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamentos() });
    },
  });
}

/** useDispatchLevantamento — #426 gesto 2: botão "Reprocessar" de um
 * levantamento em `fila_status='erro'` (mesmo endpoint do dispatch usado
 * pelo botão "Processar levantamento"). Invalida detalhe + lista geral —
 * a lista alimenta o placar da fila (posição FIFO/ativos) em outros
 * levantamentos que possam estar exibindo o mesmo snapshot. */
export function useDispatchLevantamento(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dispatchLevantamento(levId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamento(levId) });
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamentos() });
    },
  });
}

/** useReprocessarLevantamento — #434: cria o clone a partir de um
 * levantamento validado. Invalida o detalhe do ORIGINAL (vídeos saem da
 * lista dele, foram pro clone) + a lista geral (o clone novo aparece nela).
 * O caller (dialog de confirmação) navega pro clone usando o retorno.
 * Aceita `{ clonar_linhas }` (follow-up #434) -- omitir usa o default do
 * backend (true). */
export function useReprocessarLevantamento(levId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReprocessarLevantamentoInput = {}) =>
      reprocessarLevantamento(levId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamento(levId) });
      qc.invalidateQueries({ queryKey: videoQueryKeys.levantamentos() });
    },
  });
}
